import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, UsersRound, User, Calendar, Shield, MapPin, Trophy, Building2, Loader2 } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/groups?${params}`);
      setGroups(response.data.groups || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load groups');
      setGroups([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleViewDetails = async (group) => {
    setShowDetailModal(true);
    setLoadingDetails(true);
    try {
      // Fetch detailed group data including enriched member info
      const response = await axios.get(`${API_URL}/api/groups/${group.id}`);
      setSelectedGroup(response.data);
    } catch (error) {
      toast.error('Failed to load group details');
      setSelectedGroup(group); // Fallback to basic data
    } finally {
      setLoadingDetails(false);
    }
  };

  const columns = [
    {
      key: 'group',
      header: 'Group',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
            {row.avatar_url ? (
              <Avatar className="w-10 h-10">
                <AvatarImage src={row.avatar_url} alt={row.name} />
                <AvatarFallback className="bg-indigo-500/20 text-indigo-400">
                  {row.name?.substring(0, 2).toUpperCase() || 'GR'}
                </AvatarFallback>
              </Avatar>
            ) : (
              <UsersRound className="w-5 h-5 text-indigo-400" />
            )}
          </div>
          <div>
            <p className="font-medium text-white">{row.name || 'Unnamed Group'}</p>
            <p className="text-xs text-slate-500">{row.description || 'No description'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <span className="text-slate-300">{row.members?.length || row.member_count || 0}</span>
        </div>
      ),
    },
    {
      key: 'created_by',
      header: 'Created By',
      render: (row) => (
        <span className="text-slate-300">{row.created_by_name || row.created_by || '-'}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => (
        <span className="text-slate-400 text-sm">
          {row.created_at ? formatDate(row.created_at) : '-'}
        </span>
      ),
    },
    {
      key: 'privacy',
      header: 'Privacy',
      render: (row) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          row.is_private 
            ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
            : 'bg-green-500/20 text-green-400 border border-green-500/30'
        }`}>
          <Shield className="w-3 h-3" />
          {row.is_private ? 'Private' : 'Public'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`group-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem 
              onClick={() => handleViewDetails(row)}
              className="text-slate-300 focus:bg-slate-800 focus:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="groups-page">
      <PageHeader
        title="Groups"
        description="View all groups created by Lunipin app users (Owner access only)"
      />

      <SearchFilter
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by group name..."
        onReset={() => setSearch('')}
      />

      <DataTable
        columns={columns}
        data={groups}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No groups found"
      />

      {/* Group Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UsersRound className="w-5 h-5 text-indigo-400" />
              Group Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : selectedGroup && (
            <div className="space-y-6">
              {/* Group Info */}
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center overflow-hidden">
                  {(selectedGroup.logo_url || selectedGroup.avatar_url) ? (
                    <img 
                      src={selectedGroup.logo_url || selectedGroup.avatar_url} 
                      alt={selectedGroup.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <UsersRound className="w-10 h-10 text-indigo-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white">{selectedGroup.name || 'Unnamed Group'}</h3>
                  <p className="text-slate-400 mt-1">{selectedGroup.description || 'No description'}</p>
                  
                  {/* Badges Row */}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {/* Group Type */}
                    {selectedGroup.group_type && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                        <Building2 className="w-3 h-3" />
                        {selectedGroup.group_type}
                      </span>
                    )}
                    
                    {/* Privacy */}
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      selectedGroup.is_private || !selectedGroup.is_public
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                        : 'bg-green-500/20 text-green-400 border border-green-500/30'
                    }`}>
                      <Shield className="w-3 h-3" />
                      {selectedGroup.is_private || !selectedGroup.is_public ? 'Private' : 'Public'}
                    </span>
                    
                    {/* Owner Badge */}
                    {selectedGroup.is_owner && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                        Owner
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Sports */}
              {selectedGroup.sports && selectedGroup.sports.length > 0 && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    Sports
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedGroup.sports.map((sport, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm border border-indigo-500/30"
                      >
                        {typeof sport === 'string' ? sport : sport.name || sport.sport_name || 'Unknown'}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              {(selectedGroup.city || selectedGroup.state || selectedGroup.area || selectedGroup.location) && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-400" />
                    Location
                  </h4>
                  <p className="text-white">
                    {[
                      selectedGroup.area,
                      selectedGroup.city,
                      selectedGroup.state,
                      selectedGroup.country
                    ].filter(Boolean).join(', ') || selectedGroup.location || 'Not specified'}
                  </p>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedGroup.members?.length || selectedGroup.member_count || 0}</p>
                  <p className="text-sm text-slate-400">Members</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedGroup.admins?.length || 0}</p>
                  <p className="text-sm text-slate-400">Admins</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedGroup.posts_count || selectedGroup.events_count || 0}</p>
                  <p className="text-sm text-slate-400">Posts/Events</p>
                </div>
              </div>

              {/* Members List */}
              {selectedGroup.members && selectedGroup.members.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Members ({selectedGroup.members.length})
                  </h4>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {selectedGroup.members.slice(0, 20).map((member, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-800/30 rounded-lg">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={member.avatar_url || member.profile_image} />
                          <AvatarFallback className="bg-slate-700 text-slate-300 text-xs">
                            {(member.name || member.user_name || 'U').substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm text-white font-medium">{member.name || member.user_name || 'Unknown User'}</p>
                          <p className="text-xs text-slate-500">
                            {member.role || (selectedGroup.admins?.some(a => (a.user_id || a.id || a) === (member.user_id || member.id)) ? 'Admin' : 'Member')}
                          </p>
                        </div>
                        {selectedGroup.created_by === (member.user_id || member.id) && (
                          <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">Owner</span>
                        )}
                      </div>
                    ))}
                    {selectedGroup.members.length > 20 && (
                      <p className="text-sm text-slate-400 text-center py-2">
                        +{selectedGroup.members.length - 20} more members
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Created Info */}
              <div className="border-t border-slate-700 pt-4 flex items-center justify-between">
                <p className="text-sm text-slate-400">
                  Created by <span className="text-white">{selectedGroup.created_by_name || 'Unknown'}</span>
                </p>
                <p className="text-sm text-slate-500">
                  {selectedGroup.created_at ? formatDate(selectedGroup.created_at) : '-'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
